import {
  Box,
  Flex,
  useColorModeValue,
  Square,
  Tooltip,
  Input,
} from '@chakra-ui/react';
import {
  IconDefinition,
  faFlag,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TimelineDot } from './TimelineDot';
import SparkelIcon from 'src/components/common/icon/SparkelIcon';

interface TimelineEventWrapperProps {
  children: ReactNode;
  isSelected: boolean;
  isInPreview: boolean;
  isDragging: boolean;
  isFirstInSelection: boolean;
  isLastInSelection: boolean;
  isFirstInPreview: boolean;
  isLastInPreview: boolean;
  icon: IconDefinition;
  isFirstEvent?: boolean;
  isHovered?: boolean;
  isCheckPoint: boolean;
  isNextEventCheckpoint: boolean;
  handleCheckPointCreation: (name: string) => Promise<void>;
}

// Helper functions to calculate styles
const calculateBackgroundColor = (
  isDragging: boolean,
  isInPreview: boolean,
  isSelected: boolean,
  selectedBgColor: string,
  previewBgColor: string
): string | undefined => {
  if (isDragging) {
    return isInPreview ? previewBgColor : undefined;
  }
  return isSelected ? selectedBgColor : undefined;
};

const calculateBorderStyles = (
  isDragging: boolean,
  isFirstInSelection: boolean,
  isLastInSelection: boolean,
  isFirstInPreview: boolean,
  isLastInPreview: boolean,
  isSelected: boolean
) => {
  let borderTopRadius = '0';
  let borderTop = 'none';
  let borderBottomRadius = '0';
  let borderBottom = 'none';

  if (isDragging) {
    if (isFirstInPreview) {
      borderTopRadius = 'lg';
      borderTop = '2px solid';
    }
    if (isLastInPreview) {
      borderBottomRadius = 'lg';
      borderBottom = '2px solid';
    }
  } else {
    if (isFirstInSelection && isSelected) {
      borderTopRadius = 'lg';
      borderTop = '2px solid';
    }
    if (isLastInSelection && isSelected) {
      borderBottomRadius = 'lg';
      borderBottom = '2px solid';
    }
  }

  return {
    borderTopRadius,
    borderTop,
    borderBottomRadius,
    borderBottom,
  };
};

// eslint-disable-next-line complexity
export const TimelineEventWrapper: React.FC<TimelineEventWrapperProps> = ({
  children,
  isSelected,
  isInPreview,
  isDragging,
  isFirstInSelection,
  isLastInSelection,
  isFirstInPreview,
  isLastInPreview,
  icon,
  isCheckPoint,
  isNextEventCheckpoint,
  isFirstEvent = false,
  isHovered = false,
  handleCheckPointCreation,
}) => {
  const [isAddingCheckpoint, setIsAddingCheckpoint] = useState(false);
  const [checkPointName, setCheckpointName] = useState('');
  // Colors
  const selectedBgColor = useColorModeValue('blue.50', 'blue.800');
  const previewBgColor = useColorModeValue('gray.50', 'gray.700');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');
  const selectedBorderColor = useColorModeValue('blue.500', 'blue.300');
  const timelineColor = useColorModeValue('gray.400', 'gray.500');
  const timelineDotBackgroundColor = useColorModeValue('white', 'gray.800');
  const checkPointColor = useColorModeValue('cyan.500', 'cyan.200');

  const { t } = useTranslation('project-history');

  // Calculate styles
  const bgColor = calculateBackgroundColor(
    isDragging,
    isInPreview,
    isSelected,
    selectedBgColor,
    previewBgColor
  );

  // Update border styles logic for the first event
  // eslint-disable-next-line complexity
  const getBorderStyles = () => {
    // If this is the first event, never use top radius or top border,
    // as those should be on the CurrentVersionHeader
    if (isFirstEvent) {
      const { borderBottomRadius, borderBottom } = calculateBorderStyles(
        isDragging,
        isFirstInSelection,
        isLastInSelection,
        isFirstInPreview,
        isLastInPreview,
        isSelected
      );

      // If we are the first event and also a checkpoint and we are selected,
      // then we'd liks to have the top border since the header is not there to set it
      return {
        borderTopRadius: isCheckPoint && isSelected ? 'lg' : '0',
        borderTop: isCheckPoint && isSelected ? '2px solid' : 'none',
        borderBottomRadius,
        borderBottom,
        borderLeft: isSelected || isInPreview ? '2px solid' : 'none',
        borderRight: isSelected || isInPreview ? '2px solid' : 'none',
      };
    }

    // For other events, use the normal calculation
    const { borderTopRadius, borderTop, borderBottomRadius, borderBottom } =
      calculateBorderStyles(
        isDragging,
        isFirstInSelection,
        isLastInSelection,
        isFirstInPreview,
        isLastInPreview,
        isSelected
      );

    return {
      borderTopRadius,
      borderTop,
      borderBottomRadius,
      borderBottom,
      borderLeft: isSelected || isInPreview ? '2px solid' : 'none',
      borderRight: isSelected || isInPreview ? '2px solid' : 'none',
    };
  };

  const { borderTopRadius, borderTop, borderBottomRadius, borderBottom } =
    getBorderStyles();

  return (
    <Flex position="relative" flexDirection="column">
      {isCheckPoint ? (
        <Box
          position="absolute"
          left="16px"
          top="12px"
          transform="translateX(-50%)"
          zIndex={2}
        >
          <Square
            size="20px"
            color={checkPointColor}
            backgroundColor={timelineDotBackgroundColor}
            border="1px solid"
            borderColor={checkPointColor}
            style={{ rotate: '45deg' }}
          >
            <SparkelIcon
              icon={faFlag}
              color="inherit"
              size="xs"
              style={{ rotate: '-45deg' }}
            />
          </Square>
        </Box>
      ) : (
        <Box
          role="group"
          position="absolute"
          height="100%"
          width="13%"
          zIndex={10}
        >
          {/* Event dot */}
          <Box
            position="absolute"
            left="16px"
            top="12px"
            transform="translateX(-50%)"
            zIndex={2}
          >
            <TimelineDot isSelected={isSelected} icon={icon} />
          </Box>
          {/* Only show this if the current item group is not a checkpoint */}
          {!isNextEventCheckpoint ? (
            <Tooltip label={t('tooltip.add_checkpoint')}>
              <Box
                position="absolute"
                left="16px"
                cursor="pointer"
                bottom={isAddingCheckpoint ? '4' : '0px'}
                transform="translateX(-50%)"
                zIndex={2}
                visibility={isAddingCheckpoint ? 'visible' : 'hidden'}
                // visibility={'visible'}
                opacity={isAddingCheckpoint ? 1 : 0}
                transition="opacity 0.2s"
                // _hover={{ visibility: 'visible' }}
                _groupHover={{ visibility: 'visible', opacity: 1 }}
                onClick={() => {
                  // TODO: figure out how to enable editing the name as well
                  setIsAddingCheckpoint(true);
                }}
              >
                <Square
                  size="20px"
                  color={isSelected ? selectedBorderColor : timelineColor}
                  backgroundColor={
                    isSelected ? selectedBgColor : timelineDotBackgroundColor
                  }
                  border={isSelected ? '2px solid' : '1px solid'}
                  borderColor={isSelected ? selectedBorderColor : timelineColor}
                  style={{ rotate: '45deg' }}
                >
                  <SparkelIcon
                    icon={faPlus}
                    color="inherit"
                    size="xs"
                    style={{ rotate: '-45deg' }}
                  />
                </Square>
              </Box>
            </Tooltip>
          ) : null}{' '}
        </Box>
      )}

      {/* Event content */}
      <Box
        flex="1"
        backgroundColor={
          isHovered && !isSelected && !isInPreview ? hoverBgColor : bgColor
        }
        borderTopRadius={borderTopRadius}
        paddingTop={borderTopRadius === '0' ? '2px' : 0}
        borderTop={borderTop}
        borderBottomRadius={
          isHovered && !isSelected && !isInPreview ? 'lg' : borderBottomRadius
        }
        paddingBottom={borderBottomRadius === '0' ? '2px' : 0}
        paddingLeft={5}
        borderBottom={borderBottom}
        borderColor={selectedBorderColor}
        _hover={
          !isDragging && !isSelected && !isHovered
            ? { backgroundColor: hoverBgColor, borderRadius: 'md' }
            : undefined
        }
        transition="background-color 0.2s"
      >
        {children}
        {isAddingCheckpoint ? (
          <Box paddingLeft="4" mb="2">
            <Input
              aria-label="Add checkpoint"
              type="text"
              textAlign={'start'}
              size="sm"
              px={1}
              borderRadius={0}
              borderColor="chakra-border-color"
              autoFocus={true}
              onFocus={(e) => e.target.select()}
              placeholder={t('rename.checkpoint_place_holder')}
              value={checkPointName}
              onChange={(e) => {
                setCheckpointName(e.target.value);
              }}
              onBlur={async () => {
                // we should figure out how trigger submission on enter instead of just on blur
                setIsAddingCheckpoint(false);
                await handleCheckPointCreation(checkPointName);
              }}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setIsAddingCheckpoint(false);
                  handleCheckPointCreation(checkPointName);
                } else if (e.key === 'Escape') {
                  setIsAddingCheckpoint(false);
                  setCheckpointName('');
                }
              }}
            />
          </Box>
        ) : null}
      </Box>
    </Flex>
  );
};

export default TimelineEventWrapper;
